import { Delegate } from './delegate.js';

class ConnectedProducts extends HTMLElement {
  #cache = new Map();
  #delegate = null;
  #prefetchIdleId = null;

  connectedCallback() {
    if (this.#delegate) return;

    this.#delegate = new Delegate(this);
    this.#delegate.on('click', '[data-connected-product-url]', this.#onOptionClick);
    this.#schedulePrefetch();
  }

  disconnectedCallback() {
    this.#delegate?.off?.();
    this.#delegate = null;

    if (this.#prefetchIdleId != null) {
      cancelIdleCallback(this.#prefetchIdleId);
      this.#prefetchIdleId = null;
    }

    this.#cache.clear();
  }

  #schedulePrefetch() {
    const prefetch = () => this.#prefetchAll();

    if ('requestIdleCallback' in window) {
      this.#prefetchIdleId = requestIdleCallback(prefetch, { timeout: 2000 });
    } else {
      prefetch();
    }
  }

  #prefetchAll() {
    const productInfo = this.closest('product-info');
    const sectionId = productInfo?.dataset?.section || this.dataset.sectionId;
    if (!sectionId) return;

    this.querySelectorAll('[data-connected-product-url]').forEach((button) => {
      const url = button.dataset.connectedProductUrl;
      if (!url || this.#cache.has(url)) return;

      const requestUrl = `${url}?section_id=${sectionId}`;

      fetch(requestUrl)
        .then((response) => response.text())
        .then((responseText) => {
          const html = new DOMParser().parseFromString(responseText, 'text/html');
          this.#cache.set(url, html);
        })
        .catch(() => {});
    });
  }

  #onOptionClick = (event, button) => {
    if (button.classList.contains('is-selected')) return;

    const productUrl = button.dataset.connectedProductUrl;
    const optionName = button.dataset.connectedProductOptionName;
    const productInfo = this.closest('product-info');

    this.#updateSelectedValue(optionName);
    this.#setSelectedOption(button);

    if (!productInfo) {
      window.location.assign(productUrl);
      return;
    }

    const callback = productInfo.handleSwapProduct(productUrl, false, 'main-product');
    const cached = this.#cache.get(productUrl);

    if (cached) {
      callback(cached);
      button.focus();
      return;
    }

    productInfo.renderProductInfo({
      requestUrl: `${productUrl}?section_id=${productInfo.sectionId}`,
      targetId: button.id,
      callback,
    });
  };

  #updateSelectedValue(optionName) {
    const valueEl = this.querySelector('[data-selected-value]');
    if (valueEl && optionName) {
      valueEl.textContent = optionName;
    }
  }

  #setSelectedOption(button) {
    this.querySelectorAll('[data-connected-product-url]').forEach((option) => {
      option.classList.remove('is-selected');
      option.removeAttribute('aria-current');
    });

    button.classList.add('is-selected');
    button.setAttribute('aria-current', 'true');
  }
}

if (!customElements.get('connected-products')) {
  customElements.define('connected-products', ConnectedProducts);
}
